import React, { useEffect, useState } from "react";
import { Coins, Loader2, RefreshCw, Sparkles } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

// Prices are shown in local money, so the rate table and the country->currency list are operations,
// not trivia: last refresh, a manual refresh, and every currency we learned from a new signup.
export const AdminRates = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/fx").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy("refresh");
    try {
      const { data: res } = await api.post("/admin/fx/refresh");
      toast({ title: `Rates refreshed (${res.count} currencies, ${res.source})` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || "Refresh failed" }); }
    finally { setBusy(""); }
  };

  const toggleAi = async () => {
    setBusy("ai");
    try {
      const { data: res } = await api.post("/admin/fx/ai", { on: !data.aiDetect });
      setData((d) => ({ ...d, aiDetect: res.aiDetect }));
      toast({ title: res.aiDetect ? "AI currency detection on" : "AI currency detection off",
        description: res.aiDetect ? "New countries get their own currency automatically." : "Unknown countries default to USD." });
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not change that" }); }
    finally { setBusy(""); }
  };

  if (!data) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-rates">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-bold text-foreground flex items-center gap-2">
            <Coins size={18} className="text-[#16a34a]" /> Exchange rates
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="rates-updated">
            {data.count} currencies · {data.source} · refreshed every {data.refreshHours}h ·
            {data.updatedAt ? ` last ${new Date(data.updatedAt).toLocaleString()}` : " never"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={refresh} disabled={busy === "refresh"} data-testid="rates-refresh-btn"
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors disabled:opacity-60">
            {busy === "refresh" ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Refresh now
          </button>
          <button onClick={toggleAi} disabled={busy === "ai"} data-testid="rates-ai-toggle"
            className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-3 py-2 border transition-colors disabled:opacity-60 ${data.aiDetect
              ? "bg-[#e7f6ec] dark:bg-[#16341f] text-[#16a34a] border-[#16a34a]/40"
              : "bg-secondary text-muted-foreground border-border"}`}>
            {busy === "ai" ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />}
            AI detect {data.aiDetect ? "on" : "off"}
          </button>
        </div>
      </div>

      {data.missing?.length > 0 && (
        <p className="text-xs text-[#b8860b] mt-2" data-testid="rates-missing">
          Provider had no rate for: {data.missing.join(", ")}
        </p>
      )}

      {data.learned?.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-bold text-foreground mb-1.5">Currencies learned from new countries</p>
          <div className="space-y-1">
            {data.learned.slice(0, 10).map((l) => (
              <div key={l.country} className="flex items-center gap-2 text-xs" data-testid={`rate-learned-${l.currency}`}>
                <span className="font-semibold text-foreground flex-1 min-w-0 truncate">{l.country}</span>
                <span className="font-bold text-[#16a34a]">{l.currency}</span>
                <span className="text-muted-foreground">{l.source}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRates;
